// @flow
import React from "react";
import classNames from "classnames";
import startCase from "lodash/startCase";
import withStyles from "elevate-ui/withStyles";
import Typography from "elevate-ui/Typography";
import BlogAbout from "./BlogAbout";
import BlogListing from "./BlogListing";
import BlogVideo from "./BlogVideo";
import BlogOpenHouse from "./BlogOpenHouse";
import SocialLinks from "../common/SocialLinks";

import type { $TransformedBlogPost, $Site } from "flow/types";

type $Props = {
  classes: Object,
  className: string,
  post: $TransformedBlogPost,
  image: string,
  site: $Site,
  hideSocialLink: boolean,
};

const BlogPost = ({ classes, className, post, image, site }: $Props) => {
  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.postHeader}>
        <Typography type="heading3" className={classes.postTitle}>
          {post.title}
        </Typography>
        <div className={classes.postAuthorContainer}>
          {post.author &&
            post.author.picture && (
              <div
                className={classes.postAuthorImage}
                style={{
                  backgroundImage: `url('${
                    post.author.picture
                  }?fit=facearea&facepad=2&w=48&h=48')`,
                }}
              />
            )}
          <p className={classes.postAuthor}>
            by{" "}
            <span className={classes.postAuthorBold}>
              {post.author && post.author.fullName + " "}
            </span>
            {post.createdAtFormatted}
          </p>
          <SocialLinks post={post} site={site} />
        </div>
      </div>
      {/* The status here need to be copied to BlogPostSyn.js */}
      {["new", "updated", "sold", "pcg", "openHouse"].indexOf(post.entityType) >
      -1 ? (
        <div>
          <BlogListing image={image} listing={post.listing} />
          {post.entityType === "openHouse" && <BlogOpenHouse post={post} />}
        </div>
      ) : ["video"].indexOf(post.entityType) > -1 ? (
        <BlogVideo listing={post.listing} videoUrl={post.videoUrl} />
      ) : (
        <div
          className={classes.post}
          dangerouslySetInnerHTML={{
            __html: post && post.htmlFormatted,
          }}
        />
      )}
      <div className={classes.postTagsContainer}>
        {post.tags &&
          post.tags.map((tag) => {
            return (
              <a
                key={tag}
                className={classes.postTagLink}
                href={`/blog/category/${encodeURIComponent(tag)}`}
              >
                <div className={classes.postTags}>{startCase(tag)}</div>
              </a>
            );
          })}
      </div>
      <SocialLinks post={post} site={site} />
      {site.userId && <BlogAbout post={post} site={site} />}
    </div>
  );
};

export default withStyles((theme) => ({
  "@global body": {
    backgroundColor: "#fff !important",

    a: {
      textDecoration: "none",
      "&:hover": {
        textDecoration: "none !important",
      },
    },
  },
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    padding: theme.spacing.unit * 0.5,
    margin: "24px auto 96px auto",

    [theme.breakpoints(600)]: {
      maxWidth: "584px",
    },

    [theme.breakpoints(900)]: {
      maxWidth: "700px",
      margin: "72px auto 96px auto",
    },
  },

  postHeader: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    margin: "0",
    marginBottom: theme.spacing.unit * 2,
    paddingLeft: theme.spacing.unit * 0.25,
  },

  postTitle: {
    marginBottom: theme.spacing.unit * 2,
    padding: "0px 8px",
  },
  post: {
    color: theme.colors.gray["700"],
    fontSize: "18px",
    lineHeight: "1.6",
    padding: "0px 8px",

    [theme.breakpoints(600)]: {
      fontSize: "21px",
      padding: "0px",
    },

    "& h1": {
      color: theme.colors.black,
      fontWeight: "700",
      letterSpacing: ".2px",
      fontSize: "32px",
      marginTop: theme.spacing.unit * 4,
      marginBottom: theme.spacing.unit * 0.75,
    },

    "& h2": {
      fontSize: "26px",
      fontWeight: "700",
      marginTop: theme.spacing.unit * 2,
      marginBottom: theme.spacing.unit * 0.75,
    },

    "& h3": {
      fontSize: "21px",
      fontWeight: "700",
      marginTop: theme.spacing.unit * 2,
      marginBottom: theme.spacing.unit * 0.75,
    },

    "& h4": {
      fontSize: "21px",
      fontWeight: "700",
      marginTop: theme.spacing.unit * 2,
      marginBottom: theme.spacing.unit * 0.75,
    },

    "& h5": {
      fontSize: "21px",
      fontWeight: "700",
      marginTop: theme.spacing.unit * 2,
      marginBottom: theme.spacing.unit * 0.75,
    },

    "& h6": {
      fontSize: "21px",
      fontWeight: "700",
      marginTop: theme.spacing.unit * 2,
      marginBottom: theme.spacing.unit * 0.75,
    },

    "& a": {
      color: theme.colors.black,
      textDecoration: "underline",
    },

    "& ol": {
      position: "relative",
      counterReset: "item",
      marginTop: theme.spacing.unit * 2,
      marginBottom: theme.spacing.unit * 2,
    },

    "& ol li": {
      counterIncrement: "item",
      paddingLeft: theme.spacing.unit * 1.75,
      paddingBottom: theme.spacing.unit,

      "&:before": {
        content: 'counter(item) "."',
        position: "absolute",
        left: "0",
        fontWeight: "600",
      },
    },

    "& ul": {
      listStyleType: "disc",
      marginTop: theme.spacing.unit * 2,
      marginBottom: theme.spacing.unit * 2,
    },

    "& ul li": {
      marginLeft: theme.spacing.unit,
      paddingBottom: theme.spacing.unit,
    },

    "& p": {
      marginTop: theme.spacing.unit * 0.25,
      marginBottom: theme.spacing.unit * 0.75,
    },

    "& img": {
      display: "block",
      maxWidth: "100%",
      marginBottom: theme.spacing.unit,
    },

    "& img[alt='This Old House']": {
      width: "120px",
    },

    "& b": {
      fontWeight: "600",
    },

    "& strong": {
      fontWeight: "600",
    },
  },
  postAuthorContainer: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    paddingLeft: theme.spacing.unit * 0.5,
  },
  postAuthor: {
    flexShrink: "0",
    fontSize: "14px",
    color: theme.colors.gray["700"],
    paddingLeft: theme.spacing.unit * 0.5,
  },
  postAuthorImage: {
    borderRadius: "100%",
    width: "48px",
    height: "48px",
    backgroundSize: "contain",
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
  },
  postAuthorBold: {
    fontWeight: "700",
  },
  postTagsContainer: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: theme.spacing.unit * 2,
    padding: "20px 8px",

    [theme.breakpoints(600)]: {
      padding: "20px 0",
    },
  },
  postTags: {
    marginRight: theme.spacing.unit * 0.75,
    marginBottom: theme.spacing.unit * 0.75,
    fontSize: "10px",
    fontWeight: "600",
    color: theme.colors.gray["500"],
    letterSpacing: ".5px",
    padding: theme.spacing.unit * 0.5,
    borderRadius: "16px",
    backgroundColor: theme.colors.gray["100"],
    "&:hover": {
      color: theme.colors.gray["500"],
      textDecoration: "none !important",
    },
  },
  postTagLink: {
    "&:hover": {
      textDecoration: "none !important",
    },
  },
}))(BlogPost);
